import { useQuery } from '@tanstack/react-query';
import { Badge, DataTable, EmptyState, Panel, t } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { duration, percent } from '../lib/availability';
import { shortDateTime } from '../lib/format';
import { RefusalPage } from './Refusal';
import { FULL_HEIGHT } from '../lib/tables';

type Interruption = fleetState.paths['/servers/{server}/interruptions']['get']['responses']['200']['content']['application/json']['items'][number];
const TONES = { down: 'neg', scheduled: 'info', unmeasured: 'neutral' } as const;
/** The server page shows the last 30 days; the « Disponibilité » screen goes back 5 years. */
const DAYS = 30;

/**
 * D44 « Interruptions » (server page, Historique tab): every moment of the
 * last 30 days the server was not « up » — unavailable, scheduled stop, or
 * not measured — newest first, with Checkmk's cause, under the period's figure.
 */
export function InterruptionsPanel({ server }: { server: string }) {
  const list = useQuery({
    queryKey: ['server-interruptions', server],
    queryFn: async () => {
      const from = new Date(Date.now() - DAYS * 86_400_000).toISOString();
      return unwrap(await api.fleetState.GET('/servers/{server}/interruptions', { params: { path: { server }, query: { from } } }));
    },
    refetchInterval: 60_000,
  });
  if (list.error) return <RefusalPage error={list.error} />;
  const f = list.data?.figure;
  const subtitle = !f ? t('server.interruptions.subtitle', { days: DAYS })
    : f.availability === null ? t('server.interruptions.notMeasured', { days: DAYS })
      : !f.sufficient ? t('server.interruptions.insufficient', { days: DAYS, coverage: percent(f.coverage, 1) })
        : t('server.interruptions.figure', { days: DAYS, availability: percent(f.availability), coverage: percent(f.coverage, 1) });
  return (
    <Panel title={t('server.interruptions.title')} subtitle={subtitle} flush>
      <DataTable
        bordered={false}
        height={FULL_HEIGHT}
        label={t('server.interruptions.title')}
        rows={list.data?.items ?? []}
        rowKey={(i: Interruption) => `${i.state}-${i.start}`}
        loading={list.isPending}
        toolbar={false}
        density="compact"
        columns={[
          { key: 'state', label: t('server.interruptions.columns.state'), width: 150, render: (i: Interruption) => <Badge tone={TONES[i.state]}>{t(`availability.state.${i.state}`)}</Badge> },
          { key: 'start', label: t('server.interruptions.columns.window'), width: 200, render: (i: Interruption) => `${shortDateTime(i.start)} → ${i.end ? shortDateTime(i.end) : t('server.interruptions.ongoing')}` },
          { key: 'seconds', label: t('server.interruptions.columns.duration'), width: 100, align: 'right' as const, render: (i: Interruption) => duration(i.seconds) },
          { key: 'cause', label: t('server.interruptions.columns.cause'), grow: true, width: 240, render: (i: Interruption) => (i.cause ? t(`availability.cause.${i.cause}`) : '—') },
        ]}
        empty={<EmptyState icon="check_circle" title={t('server.interruptions.empty', { days: DAYS })} />}
      />
    </Panel>
  );
}
